<template>

	<v-card>
		<v-img
		height="200px"
		src="/img/about-header.jpeg"
		>
			<v-container fill-height fluid>
				<v-layout fill-height>
					<v-flex xs12 text-xs-center flexbox>
						<span class="headline" id="title">Follow Server {{name}}</span>
					</v-flex>
					<v-divider/>
				</v-layout>
			</v-container>
		</v-img>
		<v-card-title>
			<div>
				<h2 class="headline mb-0">About</h2>
				<p>
					<strong>Follow</strong> is a lightweight asset tracking system. It collects, processes and stores position and status data from tracking units.
				</p>
				<v-expansion-panel>
					<v-expansion-panel-content>
						<div slot="header">
							<h3>Features</h3>
						</div>
						<v-card>
							<v-card-text>
								<h4>Recording</h4>
								<ul>
									<li>Unlimited users</li>
									<li>Unlimited devices</li>
									<li>Self-hosted server</li>
									<li>Share tracks with other users on the same host, read/write, read-only, or even write-only</li>
									<li><s>Share tracks with users on another server</s><sup>[Coming soon]</sup></li>
									<li>Share tracks publicly. Give a unique URL to someone else and they can access your track with the permissions you specify (including writing to it, if you so choose)</li>
									<li>Hierarchical organisation. Tracks are organised in <em>channels</em>. A channel can be written to by one or more devices (and conversely, devices may write to one or more channels). Channels can be organised in a tree hierarcy, making it easier to navigate and manage.</li>
								</ul>
								<h4>Alert conditions</h4>
								<ul>
									<li>Supports geofencing</li>
									<li>Supports user-defined alert conditions, defined by scripting</li>
									<li>Advanced geofencing. For instance, a trigger a condition if two different assets are more (or less) than a certain distance apart</li>
									<li>You may associate different conditions with different channels</li>
								</ul>
								<h4>Actions</h4>
								<p>Alerts are associated with actions. When an alert occurs, all of its associated actions are triggered. Actions can be:</p>
								<ul>
									<li>Email alerts</li>
									<li>XMPP alerts</li>
									<li>Webhooks</li>
								</ul>
							</v-card-text>
						</v-card>
					</v-expansion-panel-content>
					<v-expansion-panel-content v-if="!isLoggedIn">
						<div slot="header">
							<h3>Registering</h3>
						</div>
						<v-card>
							<v-card-text>
								<p>Create an account in a few seconds by clicking on the button below.</p>
								<v-btn primary @click="showRegisterDialog">Register</v-btn>
								
								<h4 class="mt-3"
								@click="registerInfo = !registerInfo"
								style="cursor: default;"
								>More details… <v-icon>{{registerInfo? 'fa-caret-up' : 'fa-caret-down'}}</v-icon></h4>
								<v-slide-y-transition>
									<p v-show="registerInfo">All you need to do to get started is register an account.
									Registration takes literally seconds and consists of choosing:
										<ul>
											<li>a user “handle”: an alias, unique on this server, which you will use to identify yourself;</li>
											<li>a password: you do not even need to enter it twice—just use the <em>eye</em> icon to reveal it and confirm it is what you intended to type; and</li>
											<li>strictly optionally, an email address: if provided, it will be used to send password reset links (in case you forget your password) and may also be used in the future for sending system alerts if so configured. <span class="teal--text">Your email address will never be used for anything else and obviously it will not be sold or shared with third parties. You are encouraged to use a “junk-only” or throwaway address if using a public server, or not provide an address at all (you can always add one latter). For more details see the privacy policy below.</span></li>
										</ul>
									Once registered, you will be logged in automatically.
									</p>
								</v-slide-y-transition>
							</v-card-text>
						</v-card>
					</v-expansion-panel-content>
					<v-expansion-panel-content>
						<div slot="header">
							<h3>First steps</h3>
						</div>
						<v-card>
							<v-card-text>
								<div v-if="!isLoggedIn">
									<p>Already have an account?
									</p>
									<v-btn primary @click="showLoginDialog">Log in</v-btn>
								</div>
								
								<p class="mt-2">
								Once you have logged in, most interaction happens via the navigation drawer on the left hand side of the screen. The drawer may be shown or hidden by using the <v-btn class="transparent" icon disabled><v-icon color="secondary lighten-3" style="height:auto">fa-angle-double-right</v-icon></v-btn> floating button on the bottom left side of the screen.
								</p>
								
								<h4>Adding a channel and recording data</h4>
								
								<p>
								From the navigation drawer, the <v-btn flat small disabled><v-icon small>add</v-icon> <small>New channel</small></v-btn> button will let you create a <em>channel</em>. You can think of a channel as a file where data will be stored.
								</p>
								<p>
								Once you have a channel, you will want to record data into it. The best way is to use a dedicated logging device or application.<br/>
								Presently, <strong>Follow</strong> has been tested with two popular clients:
								<ul>
									<li><a href="https://osmand.net/" target="_blank">OsmAnd</a></li>
									<li><a href="https://www.traccar.org/client/" target="_blank">Traccar Client</a></li>
								</ul>
								</p>
							</v-card-text>
						</v-card>
					</v-expansion-panel-content>
					<v-expansion-panel-content>
						<div slot="header">
							<h3>Privacy</h3>
						</div>
						<v-card>
							<v-card-text>
								<markdown-viewer :text="privacyText" :sanitize="false">
								</markdown-viewer>
							</v-card-text>
						</v-card>
					</v-expansion-panel-content>
					<v-expansion-panel-content v-if="siteText">
						<div slot="header">
							<h3>Contact and site information</h3>
						</div>
						<v-card>
							<v-card-text>
								<markdown-viewer :text="siteText" :sanitize="false">
								</markdown-viewer>
							</v-card-text>
						</v-card>
					</v-expansion-panel-content>
				</v-expansion-panel>
			</div>
		</v-card-title>
	</v-card>
</template>

<style lang="stylus" scoped>
>>>#title
	padding 8px 10%
	color white
	text-shadow rgba(0, 0, 0, 0.9) 1px 1px 4px
	//background rgba(255, 255, 255, 0.2)
	//box-shadow 0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)
>>>.about
	font-family 'Helvetica Neue', Helvetica, Arial, sans-serif
	font-style normal
	font-weight 400
	font-size 12pt
	background #fff
	height 100%
	padding 1em 2em
	
</style>

<script>
import MarkdownViewer from '../components/MarkdownViewer'

export default {
	name: 'About',
	
	components: {
		MarkdownViewer
	},
	
	data () {
		return {
			name: "at " + document.location.hostname,
			siteText: null,
			privacyText: null,
			showFeatures: false,
			registerInfo: false
		}
	},
	
	computed: {
		user () {
			return this.$store.state.userData;
		},
		
		isLoggedIn () {
			return !!this.user.user_id;
		}
	},
	
	methods: {
		showRegisterDialog () {
			this.$root.$emit('dialogue', { register: true });
		},
		
		showLoginDialog () {
			this.$root.$emit('dialogue', { login: true });
		}
	},
	
	async created () {
		try {
			const resSite = await fetch("/md/site-info.md");
			this.siteText = await resSite.text();
			const resPriv = await fetch("/md/privacy.md");
			this.privacyText = await resPriv.text();
		} catch (err) {
			console.warn("Error fetching site info or privacy policy", err);
		}
	}
}

</script>
